import { useEffect, useState } from "react";
import {
  AppBox,
  AppModal,
  AppPaper,
  AppSelect,
  AppSkeletonGlobalCardList,
  AppText,
  AppTitle,
} from "../../../../../commons/components";
import { statisticsEventsOption } from "../../MatchDetailManageStatistics";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { MatchStatisticsEventEnum } from "../../../../../commons/enums/match-statistics-event-enum";
import {
  cleanUpSeasonMatchPossessionList,
  fetchSeasonMatchPossessionList,
} from "../../../../../redux/slices/football/admin/season/match/statistics/manage-season-match-possession-list-slice";
import {
  cleanUpSeasonMatchPlayersStatisticList,
  fetchSeasonMatchPlayersStatisticList,
} from "../../../../../redux/slices/football/admin/season/match/statistics/manage-season-match-players-statistic-list-slice";
import { MatchPossessionListCard } from "./MatchPossessionListCard";
import { MatchPlayersStatisticListCard } from "./MatchPlayersStatisticListCard";
import {
  IManageFootballMatchPlayerPassStatistics,
  IManageFootballMatchPlayersStatistics,
} from "../../../../../commons/models/football/admin/interface/i-manage-football-match-players-statistics";
import { IManageFootballMatchPossession } from "../../../../../commons/models/football/admin/interface/i-manage-football-match-possession";
import IconEdit from "../../../../../commons/components/icons/edit";
import { MatchStatisticsEdit } from "./MatchStatisticsEdit";
import { getStatisticsKeyString } from "../../../../../commons/utilities/match-or-matchCard-utils";
import IconBin from "../../../../../commons/components/icons/bin";
import MatchStatisticDelete from "./MatchStatisticDelete";
import MatchStatisticDeleteInprogress from "./MatchStatisticDeleteInprogress";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../../../commons/components/interface";
import { seasonMatchPossessionDelete } from "../../../../../redux/slices/football/admin/season/match/statistics/manage-season-match-possession-delete-slice";
import { seasonMatchPlayersStatisticDelete } from "../../../../../redux/slices/football/admin/season/match/statistics/manage-season-match-players-statistic-delete-slice";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import AppTabs, { TabItem } from "../../../../../commons/components/Tabs";
import { MatchTeamTypeEnum } from "../../../../../commons/enums/match-team-type-enum";
import SkeletonMatchCardList from "../../../../../commons/components/skeleton/SkeletonMatchCardList";
import { ISelectOptions } from "../../../../../commons/components/ui-components/Select";
import { MatchPlayerPassStatisticListCard } from "./MatchPlayerPassStatisticListCard";
import {
  cleanUpSeasonMatchPlayerPassStatisticList,
  fetchSeasonMatchPlayerPassStatisticList,
} from "../../../../../redux/slices/football/admin/season/match/statistics/manage-season-match-players-pass-statistic-list-slice";
import { seasonMatchPlayerPassStatisticDelete } from "../../../../../redux/slices/football/admin/season/match/statistics/manage-season-match-players-pass-statistic-delete-slice";
import { FootballMatchDetail } from "../../../../../commons/models/football/football-match-detail";
import ability from "../../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../../commons/enums/role-policy-action.enum";
import { ManageFootballTeam } from "../../../../../commons/models/football/admin/manage-football-team";
import { useParams } from "react-router";
import { MatchStatusEnum } from "../../../../../commons/enums/match-status-enum";

interface MatchStatisticsListProps {
  match: FootballMatchDetail;
  statisticMatchEvent: string;
  onSelectedEvent: (event: string) => void;
}

const MatchStatisticsList = ({ match, statisticMatchEvent, onSelectedEvent }: MatchStatisticsListProps) => {
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { teamId } = useParams();
  const [tabItems, setTabItems] = useState<TabItem[]>([]);
  const [teamType, setTeamType] = useState(MatchTeamTypeEnum.Home);
  const { seasonMatchPossessionList, seasonMatchPossessionListStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchPossessionList
  );
  const { seasonMatchPlayersStatisticList, seasonMatchPlayersStatisticListStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchPlayersStatisticList
  );
  const { seasonMatchPlayerPassStatisticList, seasonMatchPlayerPassStatisticListStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchPlayerPassStatisticList
  );
  const { seasonMatchPlayersStatisticDeleteStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchPlayersStatisticDelete
  );
  const { seasonMatchPlayerPassStatisticDeleteStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchPlayerPassStatisticDelete
  );
  const { seasonMatchPossessionDeleteStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchPossessionDelete
  );
  const { seasonMatchPlayersStatisticEditResponseStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchPlayersStatisticEdit
  );
  const { seasonMatchPlayerPassStatisticEditResponseStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchPlayerPassStatisticEdit
  );
  const { seasonMatchPossessionEditResponseStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchPossessionEdit
  );
  const [selectedStatisticOptionValue, setSelectedStatisticOptionValue] = useState<string>(statisticMatchEvent);
  const [currentStatisticOption, setCurrentStatisticOption] = useState<ISelectOptions>(statisticsEventsOption[0]);
  const [selectedPlayersStatistic, setSelectedPlayersStatistic] =
    useState<IManageFootballMatchPlayersStatistics | null>(null);
  const [selectedPlayerPassStatistic, setSelectedPlayerPassStatistic] =
    useState<IManageFootballMatchPlayerPassStatistics | null>(null);
  const [selectedPossession, setSelectedPossession] = useState<IManageFootballMatchPossession | null>(null);
  const [isEditMatchStatistic, setEditMatchStatistic] = useState(false);
  const [isDeleteMatchStatistic, setDeleteMatchStatistic] = useState(false);
  const [filteredPlayerStatistics, setFilteredPlayerStatistics] = useState<IManageFootballMatchPlayersStatistics[]>([]);
  const [filteredPlayerPassStatistics, setFilteredPlayerPassStatistics] = useState<
    IManageFootballMatchPlayerPassStatistics[]
  >([]);
  const canUpdate =
    ability.can(RolePolicyActionEnum.UPDATE, match) ||
    ability.can(RolePolicyActionEnum.UPDATE, new ManageFootballTeam({ id: Number(teamId) }));

  useEffect(() => {
    handleSelectChange(statisticMatchEvent);
    const matchEvent = statisticsEventsOption.find((eventObject) => eventObject.value === statisticMatchEvent);
    if (matchEvent) {
      setCurrentStatisticOption(matchEvent);
    }
  }, [statisticMatchEvent]);

  useEffect(() => {
    if (match) {
      setTabItems([
        {
          label: match.homeTeam?.shortName || "",
          value: MatchTeamTypeEnum.Home,
          icon: match.homeTeam?.logo,
        },
        {
          label: match.awayTeam?.shortName || "",
          value: MatchTeamTypeEnum.Away,
          icon: match.awayTeam?.logo,
        },
      ]);
    }
  }, [match]);

  useEffect(() => {
    dispatch(cleanUpSeasonMatchPlayersStatisticList());
    dispatch(cleanUpSeasonMatchPlayerPassStatisticList());
    dispatch(cleanUpSeasonMatchPossessionList());
    if (match && match.id && match.season.id) {
      onSelectedEvent(selectedStatisticOptionValue);
      if (selectedStatisticOptionValue === MatchStatisticsEventEnum.Possession) {
        dispatch(
          fetchSeasonMatchPossessionList({
            matchId: match.id,
            seasonId: match.season.id,
            urlParams: {
              hostTeamId: teamId?Number(teamId):undefined,
            },
          })
        );
      } else if (selectedStatisticOptionValue === MatchStatisticsEventEnum.Passes) {
        dispatch(
          fetchSeasonMatchPlayerPassStatisticList({
            matchId: match.id,
            seasonId: match.season.id,
            urlParams: {
              hostTeamId: teamId?Number(teamId):undefined,
            },
          })
        );
      } else {
        const plyersStatsParams = {
          hostTeamId: teamId?Number(teamId):undefined,
          key: selectedStatisticOptionValue,
        };
        dispatch(
          fetchSeasonMatchPlayersStatisticList({
            matchId: match.id,
            seasonId: match.season.id,
            urlParams: plyersStatsParams,
          })
        );
      }
    }
  }, [selectedStatisticOptionValue, match]);

  useEffect(() => {
    if (seasonMatchPlayersStatisticList.length > 0) {
      const filteredStats = seasonMatchPlayersStatisticList
        .all()
        .filter((stats) =>
          teamType === MatchTeamTypeEnum.Home
            ? stats.team.id === match.homeTeam?.id
            : stats.team.id === match.awayTeam?.id
        );
      setFilteredPlayerStatistics(filteredStats);
    } else {
      setFilteredPlayerStatistics([]);
    }
  }, [seasonMatchPlayersStatisticList, teamType]);

  useEffect(() => {
    if (seasonMatchPlayerPassStatisticList.length > 0) {
      const filteredStats = seasonMatchPlayerPassStatisticList
        .all()
        .filter((stats) =>
          teamType === MatchTeamTypeEnum.Home
            ? stats.team.id === match.homeTeam?.id
            : stats.team.id === match.awayTeam?.id
        );
      setFilteredPlayerPassStatistics(filteredStats);
    } else {
      setFilteredPlayerPassStatistics([]);
    }
  }, [seasonMatchPlayerPassStatisticList, teamType]);

  const handleTabChange = (args: TabItem) => {
    setTeamType(args.value as MatchTeamTypeEnum);
  };

  const handleSelectChange = (value: string) => {
    if (value !== selectedStatisticOptionValue) {
      setSelectedStatisticOptionValue(value);
    }
  };

  const handleMatchPlayersStatisticsDelete = (playersStats: IManageFootballMatchPlayersStatistics) => {
    setSelectedPlayersStatistic(playersStats);
    setDeleteMatchStatistic(true);
  };
  const handleMatchPlayersStatisticsEdit = (playersStats: IManageFootballMatchPlayersStatistics) => {
    setSelectedPlayersStatistic(playersStats);
    setEditMatchStatistic(true);
  };

  const handleMatchPlayerPassStatisticsDelete = (playersStats: IManageFootballMatchPlayerPassStatistics) => {
    setSelectedPlayerPassStatistic(playersStats);
    setDeleteMatchStatistic(true);
  };
  const handleMatchPlayerPassStatisticsEdit = (playersStats: IManageFootballMatchPlayerPassStatistics) => {
    setSelectedPlayerPassStatistic(playersStats);
    setEditMatchStatistic(true);
  };

  const handleMatchPossessionDelete = (possession: IManageFootballMatchPossession) => {
    setSelectedPossession(possession);
    setDeleteMatchStatistic(true);
  };
  const handleMatchPossessionEdit = (possession: IManageFootballMatchPossession) => {
    setSelectedPossession(possession);
    setEditMatchStatistic(true);
  };

  const handleDeleteConfirm = (e: Event) => {
    e.preventDefault();
    if (match) {
      setDeleteMatchStatistic(false);
      if (selectedPossession) {
        dispatch(
          seasonMatchPossessionDelete({
            seasonId: match.season.id,
            matchId: match.id,
            possessionId: selectedPossession.id,
            urlParams: {
              hostTeamId: teamId?Number(teamId):undefined,
            },
          })
        )
          .unwrap()
          .then(() => {
            dispatch(
              fetchSeasonMatchPossessionList({
                matchId: match.id,
                seasonId: match.season.id,
                urlParams: {
                  hostTeamId: teamId?Number(teamId):undefined,
                },
              })
            );
            addSnackbar({
              key: "possession-delete",
              text: "Statistics Deleted Successfully",
              variant: "success",
            });
            handleReset();
          })
          .catch(handleError);
      }
      if (selectedPlayersStatistic) {
        dispatch(
          seasonMatchPlayersStatisticDelete({
            seasonId: match.season.id,
            matchId: match.id,
            playerId: selectedPlayersStatistic.player.id,
            statisticId: selectedPlayersStatistic.id,
            urlParams: {
              hostTeamId: teamId?Number(teamId):undefined,
            },
          })
        )
          .unwrap()
          .then(() => {
            dispatch(
              fetchSeasonMatchPlayersStatisticList({
                matchId: match.id,
                seasonId: match.season.id,
                urlParams: {
                  hostTeamId: teamId?Number(teamId):undefined,
                  key: selectedPlayersStatistic.key,
                },
              })
            );
            addSnackbar({
              key: "stats-delete",
              text: "Statistics Deleted Successfully",
              variant: "success",
            });
            handleReset();
          })
          .catch(handleError);
      }
      if (selectedPlayerPassStatistic) {
        dispatch(
          seasonMatchPlayerPassStatisticDelete({
            seasonId: match.season.id,
            matchId: match.id,
            playerId: selectedPlayerPassStatistic.player.id,
            urlParams: {
              hostTeamId: teamId?Number(teamId):undefined,
            },
          })
        )
          .unwrap()
          .then(() => {
            dispatch(
              fetchSeasonMatchPlayerPassStatisticList({
                matchId: match.id,
                seasonId: match.season.id,
                urlParams: {
                  hostTeamId: teamId?Number(teamId):undefined,
                },
              })
            );
            addSnackbar({
              key: "pass-stats-delete",
              text: "Statistics Deleted Successfully",
              variant: "success",
            });
            handleReset();
          })
          .catch(handleError);
      }
    }
  };

  const handleError = (error: AxiosError<IServerErrorResponse>) => {
    addSnackbar({
      key: "error",
      text: error?.response?.data.message,
      variant: "danger",
    });
  };

  const handleReset = () => {
    setEditMatchStatistic(false);
    setSelectedPossession(null);
    setSelectedPlayersStatistic(null);
    setSelectedPlayerPassStatistic(null);
  };

  return (
    <>
      <AppBox flexDirection="column" gap="xs">
        {match.status !== MatchStatusEnum.Upcoming && (
          <AppBox justifyContent="space-between">
            <AppBox>
              <AppTitle as="h5">Match Statistics</AppTitle>
            </AppBox>
            <AppBox className="w-1/2" justifyContent="end">
              <AppSelect
                className="w-1/2"
                options={statisticsEventsOption}
                onChange={(option) => handleSelectChange(option.value.toString())}
                currentOption={currentStatisticOption}
              />
            </AppBox>
          </AppBox>
        )}
        {match.status !== MatchStatusEnum.Upcoming && statisticMatchEvent === MatchStatisticsEventEnum.Possession && (
          <AppBox flexDirection="column" gap="2xs">
            <AppTitle as="h6">Possession</AppTitle>
            {seasonMatchPossessionListStatus === StatusEnum.Loading ? (
              <SkeletonMatchCardList noTitle numberOfItems={5} />
            ) : (
              <>
                {seasonMatchPossessionList.length > 0 ? (
                  <AppBox flexDirection="column" gap="sm">
                    {seasonMatchPossessionList.mapArray((possession, index) => (
                      <MatchPossessionListCard
                        match={match}
                        possessionData={possession}
                        key={index}
                        isEditable={index === 0}
                        onDelete={handleMatchPossessionDelete}
                        onEdit={handleMatchPossessionEdit}
                      />
                    ))}
                  </AppBox>
                ) : (
                  <AppText>There are not any statistics to display.</AppText>
                )}
              </>
            )}
          </AppBox>
        )}
        {match.status !== MatchStatusEnum.Upcoming && statisticMatchEvent !== MatchStatisticsEventEnum.Possession && (
          <AppBox flexDirection="column" gap="xs">
            <AppBox>
              <AppTabs
                as="button"
                tabItemClassName="tab-big"
                onClick={handleTabChange}
                activeValue={teamType}
                tabItems={tabItems}
                variant="filled"
                shadow="xs"
                isFilterTabs
              />
            </AppBox>
            <AppBox flexDirection="column" gap="2xs">
              {statisticMatchEvent === MatchStatisticsEventEnum.Interception && (
                <>
                  <AppTitle as="h6">{getStatisticsKeyString(selectedStatisticOptionValue)}</AppTitle>
                  {seasonMatchPlayersStatisticListStatus === StatusEnum.Loading ? (
                    <AppSkeletonGlobalCardList numberOfItems={5} withText noTitle withAvatar />
                  ) : (
                    <>
                      {filteredPlayerStatistics.length > 0 ? (
                        <AppBox flexDirection="column" gap="sm">
                          {filteredPlayerStatistics.map((playerStats, index) => (
                            <MatchPlayersStatisticListCard
                              canUpdate={canUpdate}
                              playerStatsData={playerStats}
                              key={index}
                              onDelete={handleMatchPlayersStatisticsDelete}
                              onEdit={handleMatchPlayersStatisticsEdit}
                            />
                          ))}
                        </AppBox>
                      ) : (
                        <AppText>There are not any statistics to display.</AppText>
                      )}
                    </>
                  )}
                </>
              )}
              {statisticMatchEvent === MatchStatisticsEventEnum.Passes && (
                <>
                  <AppTitle as="h6">{getStatisticsKeyString(selectedStatisticOptionValue)}</AppTitle>
                  {seasonMatchPlayerPassStatisticListStatus === StatusEnum.Loading ? (
                    <AppSkeletonGlobalCardList numberOfItems={5} withText noTitle withAvatar />
                  ) : (
                    <>
                      {filteredPlayerPassStatistics.length > 0 ? (
                        <AppBox flexDirection="column" gap="sm">
                          {filteredPlayerPassStatistics.map((playerStats, index) => (
                            <MatchPlayerPassStatisticListCard
                              canUpdate={canUpdate}
                              playerStatsData={playerStats}
                              key={index}
                              onDelete={handleMatchPlayerPassStatisticsDelete}
                              onEdit={handleMatchPlayerPassStatisticsEdit}
                            />
                          ))}
                        </AppBox>
                      ) : (
                        <AppText>There are not any statistics to display.</AppText>
                      )}
                    </>
                  )}
                </>
              )}
            </AppBox>
          </AppBox>
        )}
        {ability.cannot(RolePolicyActionEnum.UPDATE, match) &&
          ability.cannot(RolePolicyActionEnum.UPDATE, new ManageFootballTeam({ id: Number(teamId) })) &&
          match.status === MatchStatusEnum.Upcoming && (
            <AppBox flexDirection="column" gap="xs">
              <AppTitle as="h5">Match Statistics</AppTitle>
              <AppPaper shadow="xs">
                <AppBox flexDirection="column" alignItems="center" gap="sm" justifyContent="center" p="lg">
                  <AppBox alignItems="center" flexDirection="column" justifyContent="center" gap="2xs">
                    <AppTitle as="h6">No Info Available</AppTitle>
                    <AppText textAlign="center">
                      Updates for this match are not available yet. Please check back closer to kickoff for real-time
                      statistics.
                    </AppText>
                  </AppBox>
                </AppBox>
              </AppPaper>
            </AppBox>
          )}
      </AppBox>
      <AppModal
        opened={isEditMatchStatistic}
        onClose={handleReset}
        title="Edit Statistic"
        titleIcon={<IconEdit />}
        withLoadingButton={
          seasonMatchPossessionEditResponseStatus === StatusEnum.Loading ||
          seasonMatchPlayersStatisticEditResponseStatus === StatusEnum.Loading ||
          seasonMatchPlayerPassStatisticEditResponseStatus === StatusEnum.Loading
        }
        withoutButtons
      >
        <MatchStatisticsEdit
          match={match}
          statisticData={
            (selectedStatisticOptionValue === MatchStatisticsEventEnum.Possession && selectedPossession) ||
            (selectedStatisticOptionValue === MatchStatisticsEventEnum.Passes && selectedPlayerPassStatistic) ||
            (selectedPlayersStatistic as IManageFootballMatchPlayersStatistics)
          }
          statisticMatchEvent={selectedStatisticOptionValue}
          onEdit={handleReset}
          onCancel={handleReset}
        />
      </AppModal>
      <AppModal
        opened={isDeleteMatchStatistic}
        onClose={(e) => {
          setDeleteMatchStatistic(false);
          setSelectedPossession(null);
        }}
        title="Want To Delete This Statistic?"
        titleIcon={<IconBin />}
        iconColor="danger"
        confirmButtonLabel="Delete"
        onSave={(e) => {
          handleDeleteConfirm(e as Event);
        }}
      >
        <MatchStatisticDelete />
      </AppModal>
      <AppModal
        opened={
          seasonMatchPlayerPassStatisticDeleteStatus === StatusEnum.Loading ||
          seasonMatchPlayersStatisticDeleteStatus === StatusEnum.Loading ||
          seasonMatchPossessionDeleteStatus === StatusEnum.Loading
        }
        title="Deleting Statistic"
        titleIcon={<IconBin />}
        iconColor="danger"
        withoutCancelButton
        withoutButtons
        withoutCloseButton
      >
        <MatchStatisticDeleteInprogress />
      </AppModal>
    </>
  );
};

export default MatchStatisticsList;
